import type { JSONContent } from '@tiptap/core';

import { ParagraphElement, type TParagraphElement } from '@/paragraphElement';
import type { TPostElement } from '@/postElement';
import type { SerializableNode } from '@/serializer';
import RedirectLink from '@/tagsElement/components/RedirectLink';
import tagSlug from '@/tagsElement/utils/tagSlug';
import { SerializeTextLeaf, type TTextLeaf } from '@/textLeaf';
import type { ContentTag, DreamRenderElementProps } from '@/types';
import { parsePostsCategory } from '@/utils/parsePostsCategory';

interface Props {
  tags: ContentTag[];
  node: JSONContent & SerializableNode;
  postNode?: TPostElement;
  isLive: boolean;
  isInsideLink?: boolean;
  attributes?: DreamRenderElementProps<TParagraphElement>['attributes'];
}

const ALLOWED_CATEGORY_GROUP_TYPES = ['free_selection', 'latest', 'archive'];

export const ContentTags = ({ tags, postNode, node, isLive, isInsideLink = false, attributes }: Props) => {
  if (!tags || tags.length === 0) return null;

  const postsCategories = parsePostsCategory(postNode?.attrs?.postsCategory || '');

  const postGroupType = postNode?.attrs?.postGroupType || '';
  const isAllowedCategoryGroupType = ALLOWED_CATEGORY_GROUP_TYPES.includes(postGroupType);

  const filteredTags =
    isAllowedCategoryGroupType && postsCategories.length > 0
      ? tags.filter((tag) => postsCategories.includes(tag?.display || ''))
      : tags;

  if (filteredTags.length === 0) return null;

  const wrapperProps = {
    className: 'relative flex w-fit max-w-full flex-wrap',
    ...attributes,
    style: { gap: node?.attrs?.postTagsGap ?? '4px' },
  };

  if (node?.attrs?.postTagsTruncateListEnabled) {
    const tagsCount = filteredTags.length;
    const tagsToShow = filteredTags[0];
    const tagsRemaining = tagsCount - 1;
    const textNode = {
      ...node?.content?.[0],
      text: tagsToShow.display,
    } as TTextLeaf;

    return (
      <div {...wrapperProps}>
        {isLive ? (
          <RedirectLink to={`/t/${tagSlug(tagsToShow?.slug || '', tagsToShow.display)}`} isInsideLink={isInsideLink}>
            <ParagraphElement element={node as TParagraphElement}>
              <SerializeTextLeaf leaf={textNode} />
            </ParagraphElement>
          </RedirectLink>
        ) : (
          <span>
            <ParagraphElement element={node as TParagraphElement}>
              <SerializeTextLeaf leaf={textNode} />
            </ParagraphElement>
          </span>
        )}
        {tagsRemaining > 0 && (
          <ParagraphElement element={node as TParagraphElement}>
            <SerializeTextLeaf
              leaf={{
                ...textNode,
                text: `+${tagsRemaining}`,
              }}
            />
          </ParagraphElement>
        )}
      </div>
    );
  }

  return (
    <div {...wrapperProps}>
      {filteredTags.map((tag) => {
        const textNode = {
          ...node?.content?.[0],
          text: tag.display,
        } as TTextLeaf;

        return isLive ? (
          <RedirectLink key={tag.id} to={`/t/${tagSlug(tag?.slug || '', tag.display)}`} isInsideLink={isInsideLink}>
            <ParagraphElement element={node as TParagraphElement}>
              <SerializeTextLeaf leaf={textNode} />
            </ParagraphElement>
          </RedirectLink>
        ) : (
          <span key={tag.id}>
            <ParagraphElement element={node as TParagraphElement}>
              <SerializeTextLeaf leaf={textNode} />
            </ParagraphElement>
          </span>
        );
      })}
    </div>
  );
};
